<script>
import RexModalSelect from '@/rex-components/rex-modal-select.vue';
import LogGrokPatternForm from '@/components/Log/LogGrokPatternForm.vue';
import LogSavedSearchForm from '@/components/Log/LogSavedSearchForm.vue';
import {
  grokAddActions,
  savedSearchAddActions,
} from '@/components/Log/log.types';
import { LOGService } from '@/services/api/log.service';
import { notificationError, notificationSucess } from '@/utils/notification';
import {
  ADD_GROK_PATTERN,
  ADD_SAVED_SEARCH,
  GROK,
  VIEW_SAVED_SEARCHES_TAB_KEY,
  TABLE_QUERY_TYPE,
  METRIC_QUERY_TYPE,
  LOG_TYPE_RAW,
} from '@/components/Log/log.constants';
import { mapMutations, mapState, mapGetters } from 'vuex';
import {
  LOG_RESOURCE_GROUP,
  ANALYTICS_RESOURCE_GROUP,
} from '@/router/constant';
import { usePermissions } from '@/hooks/usePermissions';

export default {
  components: {
    RexModalSelect,
    LogGrokPatternForm,
    LogSavedSearchForm,
  },
  data() {
    return {
      options: grokAddActions,
      openForm: false,
      currForm: ADD_GROK_PATTERN,
      isLoading: false,
      disableBtn: false,
    };
  },
  methods: {
    ...mapMutations('logState/logCommonState', [
      'setReloadGrokTable',
      'setReloadSavedSearchTable',
    ]),
    onOptionSelect(value) {
      this.currForm = value;
      this.openForm = true;
    },
    async onCreateGrok(grokDetails) {
      try {
        let ruleData = {
          rule_name: grokDetails.patternName,
          key: {
            service_id: grokDetails.source,
            is_group: grokDetails.isGroup,
          },
          parser_type: GROK,
          attributes: {
            parsing_pattern: grokDetails.grokPattern,
          },
          log_type: LOG_TYPE_RAW,
        };
        if (!grokDetails.isGroup) {
          ruleData.key.tag_name = grokDetails.tag;
        }
        const { data } = await LOGService.addLogRule(ruleData);

        if (data.success) {
          this.setReloadGrokTable(true);
          notificationSucess(this.$t('log.createGrokPatternSuccMsg'));
        } else {
          notificationError(this.$t('log.createGrokPatternErrMsg'));
        }
        this.openForm = false;
      } catch (error) {
        console.log(error);
        notificationError(this.$t('log.createGrokPatternErrMsg'));
        this.openForm = false;
      }
    },
    async onCreateSavedSearch(searchDetails) {
      try {
        this.isLoading = true;
        const searchData = {
          savedQueryName: searchDetails.searchName,
          query: searchDetails.query,
          team_id: this.teamID,
          capability: this.getCapabilityType,
        };
        const { data } = await LOGService.addSavedSearchData(searchData);

        if (data.success) {
          (!this.$route.query.queryExec ||
            this.$route.query.queryExec?.length === 0) &&
            this.setReloadSavedSearchTable(true);
          notificationSucess(this.$t('log.createSavedSearchSuccMsg'));
          this.openForm = false;
        } else {
          notificationError(this.$t('log.createSavedSearchErrMsg'));
        }
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        const formErrMsg =
          (error.status === 400 && error?.data?.errors?.message) ||
          this.$t('log.createSavedSearchErrMsg');
        notificationError(formErrMsg);
      }
    },
  },
  computed: {
    ...mapState('globalState/user', ['teamID']),
    ...mapState('logState/logCommonState', [
      'queryEnableSavedSearchType',
      'isADType',
    ]),
    ...mapGetters('logState/logCommonState', ['getCapabilityType']),
    getSourceId() {
      return this.$route.query.source
        ? this.$route.query.source?.split(',')[0]
        : '';
    },
    getSourceGroup() {
      return this.$route?.query?.isGroup ?? false;
    },
    getSourceLabel() {
      return this.$route.query.source
        ? this.$route.query.source?.split(',')[1]
        : '';
    },
    initGrokValues() {
      return {
        source: this.getSourceId,
        tag: this.$route.query.tag,
        isGroup: this.getSourceGroup,
        label: this.getSourceLabel,
      };
    },
    enableSaveSearchForms() {
      return (
        this.isADType ||
        this.currForm === ADD_SAVED_SEARCH ||
        this.queryEnableSavedSearchType === METRIC_QUERY_TYPE ||
        this.queryEnableSavedSearchType === TABLE_QUERY_TYPE
      );
    },
    initSavedSearchValues() {
      return this.queryEnableSavedSearchType
        ? { queryStr: this.$route.query.queryExec }
        : {};
    },
    hasWritePermission() {
      const { hasCapabilityWritePermission } = usePermissions();

      return hasCapabilityWritePermission(
        this.isADType ? ANALYTICS_RESOURCE_GROUP : LOG_RESOURCE_GROUP,
      );
    },
  },
  watch: {
    '$route.query.queryExec': {
      immediate: true,
      handler(newValue) {
        this.disableBtn =
          newValue?.length > 0 && !this.queryEnableSavedSearchType;
        if (newValue?.length === 0) {
          this.options =
            this.$route.query.currView === VIEW_SAVED_SEARCHES_TAB_KEY
              ? savedSearchAddActions()
              : grokAddActions();
        }
      },
    },
    '$route.query.currView': {
      immediate: true,
      handler(newValue) {
        if (newValue === VIEW_SAVED_SEARCHES_TAB_KEY || this.isADType) {
          this.options = savedSearchAddActions();
        } else {
          this.options = grokAddActions();
        }
      },
    },
    queryEnableSavedSearchType: {
      immediate: true,
      handler(newVal) {
        if (this.$route.query.queryExec?.length > 0) {
          this.disableBtn = !newVal;
          this.options = savedSearchAddActions();
        }
      },
    },
  },
};
</script>

<template>
  <div v-if="hasWritePermission">
    <RexModalSelect
      id="log-actions-add"
      :options="options"
      width="9rem"
      @input="onOptionSelect"
      :disabled="disableBtn"
    >
      <template #selected-field>
        <div
          class="add-dropdown-box"
          :class="disableBtn ? 'add-box-disabled' : 'add-box-active'"
        >
          <div
            class="add-content"
            :class="disableBtn ? 'add-content-disabled' : 'add-content-active'"
          >
            <div class="plus-icon">
              <BaseIcon
                name="plus"
                width="1.125rem"
                height="1.125rem"
                :iconColor="disableBtn ? 'rgb(45, 48, 50)' : 'rgb(245, 150, 0)'"
              />
            </div>
            <div>{{ $t('log.add') }}</div>
          </div>
        </div>
      </template>
    </RexModalSelect>

    <LogGrokPatternForm
      v-if="!enableSaveSearchForms && openForm"
      :initValues="initGrokValues"
      @onClose="openForm = false"
      @onSubmit="onCreateGrok"
    />

    <LogSavedSearchForm
      v-if="enableSaveSearchForms && openForm"
      @onSubmit="onCreateSavedSearch"
      @onClose="
        () => {
          openForm = false;
        }
      "
      :isLoading="isLoading"
      :initValues="initSavedSearchValues"
      @setLoading="(val) => (isLoading = val)"
    />
  </div>
</template>

<style lang="scss" scoped>
.add-dropdown-box {
  @include flex-center;
  border-radius: 0.25rem;
  height: 2.5rem;
}
.add-box-active {
  border: 0.063rem solid $color-brand-accent;
  cursor: pointer;
}
.add-box-disabled {
  pointer-events: none;
  cursor: not-allowed;
  background-color: $button-disabled-light;
  color: $bg-primary;
}
.add-content {
  @include flex-center;
  color: $color-brand-accent;
  padding: 0.625rem 0.813rem;
}
.add-content-active {
  color: $color-brand-accent;
}
.add-content-disabled {
  color: $bg-primary;
}
.add-content:after {
  margin-left: 0.75rem;
  content: '';
  border-left: 0.3125rem solid transparent;
  border-right: 0.3125rem solid transparent;
  border-top: 0.3125rem solid $color-brand-accent;
}
.add-content-active:after {
  border-top: 0.3125rem solid $color-brand-accent;
}
.add-content-disabled:after {
  border-top: 0.3125rem solid $bg-primary;
}
.plus-icon {
  margin-right: 0.713rem;
}
:deep(.rex-service-sel) {
  border: none;
}
:deep(.items) {
  margin-top: 0.344rem;
  background-color: $bg-dark-black;
  border-radius: 0.188rem;
  overflow: hidden;
  right: 0;
  left: auto;
  width: 10.625rem;
}
:deep(.plus-icon > span) {
  @include flex-center;
}
</style>
